import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { NaturezaMovimentacaoEnum } from '../../../shared/enums/financas/natureza-movimentacao.enum';
import { TipoContaEnum } from '../../../shared/enums/financas/tipo-conta.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ConciliacaoBancariaService } from '../../../shared/services/apis/financas/conciliacao-bancaria.service';
import { ExtratoBancarioContaItem } from '../../../shared/services/responses/financas/extrato-bancario-contas.response';
import { ExtratoBancarioIgnoradoItem } from '../../../shared/services/responses/financas/get-lancamentos-ignorados.response';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSortModule } from '@angular/material/sort';

@Component({
  selector: 'app-conciliacao-bancaria-ignorados',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
    ConfirmModalComponent,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatDatepickerModule,
    MatSortModule,
    AbntPipe,
  ],
  templateUrl: './conciliacao-bancaria-ignorados.component.html',
  styleUrls: ['./conciliacao-bancaria-ignorados.component.css'],
})
export class ConciliacaoBancariaIgnoradosComponent implements OnInit {
  moduloEnum = ModuloEnum.Financas;
  funcionalidadeEnum = FuncionalidadeEnum.Financas_ConciliacaoBancaria;
  naturezaEnum = NaturezaMovimentacaoEnum;

  idContaFinanceira!: number;
  nomeBanco = '';
  agencia = '';
  numeroConta = '';

  _contas: ExtratoBancarioContaItem[] = [];
  isLoadingContas = false;

  isLoading = false;
  isRestaurando = false;
  _temMais = false;
  _paginaAtual = 0;

  pesquisa = '';

  displayedColumns: string[] = ['select', 'data', 'descricao', 'fitid', 'valor', 'actions'];
  _dataSource = new DataSourceOrdenacaoInsensivel<ExtratoBancarioIgnoradoItem>([]);
  selection = new SelectionModel<ExtratoBancarioIgnoradoItem>(true, []);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private conciliacaoBancariaService: ConciliacaoBancariaService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    const param = this.route.snapshot.paramMap.get('idContaFinanceira');
    this.idContaFinanceira = Number(param);
    this.carregarContas();
    this.carregarIgnorados(true);
  }

  private carregarContas(): void {
    this.isLoadingContas = true;
    this.conciliacaoBancariaService.getContas().subscribe({
      next: (res) => {
        this.isLoadingContas = false;
        if (!res.isError) this._contas = res.contas;
      },
      error: () => { this.isLoadingContas = false; },
    });
  }

  onContaChange(idContaFinanceira: number): void {
    this.idContaFinanceira = idContaFinanceira;
    this.router.navigate(
      ['/financas/conciliacao-bancaria/ignorados', idContaFinanceira],
      { replaceUrl: true }
    );
    this.pesquisa = '';
    this.carregarIgnorados(true);
  }

  carregarIgnorados(resetar = true): void {
    if (resetar) {
      this._paginaAtual = 0;
      this.selection.clear();
    }
    this.isLoading = true;

    this.conciliacaoBancariaService.getLancamentosIgnorados({
      idContaFinanceira: this.idContaFinanceira,
      pesquisa: this.pesquisa.trim() || null,
      pageIndex: this._paginaAtual,
    }).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.nomeBanco = res.nomeBanco;
        this.agencia = res.agencia;
        this.numeroConta = res.numeroConta;
        this._temMais = res.temMais;

        if (resetar) {
          this._dataSource.data = res.itens;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...res.itens];
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.alertService.show('Erro', err.message, AlertType.error);
      },
    });
  }

  proximaPagina(): void {
    this._paginaAtual++;
    this.carregarIgnorados(false);
  }

  isAllSelected(): boolean {
    return this.selection.selected.length === this._dataSource.data.length;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this._dataSource.data.forEach(row => this.selection.select(row));
    }
  }

  checkboxLabel(row?: ExtratoBancarioIgnoradoItem): string {
    if (!row) return `${this.isAllSelected() ? 'deselecionar' : 'selecionar'} todos`;
    return `${this.selection.isSelected(row) ? 'deselecionar' : 'selecionar'} linha`;
  }

  restaurarSelecionados(): void {
    const ids = this.selection.selected.map(i => i.idExtratoBancario);
    if (!ids.length) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '400px',
      data: {
        title: 'Restaurar lançamentos',
        innerHtml: `<p>Tem certeza que deseja restaurar <strong>${ids.length}</strong> lançamento(s) ignorado(s)?</p><p>Eles voltarão a aparecer na lista de pendências para conciliação.</p>`,
        buttonConfirm: 'Restaurar',
        buttonClose: 'Cancelar',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.isRestaurando = true;
      this.conciliacaoBancariaService.restaurarIgnorados({ idsExtratoBancario: ids }).subscribe({
        next: (res) => {
          this.isRestaurando = false;
          if (res.isError) {
            this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
            return;
          }
          const qtd = ids.length;
          this.alertService.show('Sucesso', `${qtd} lançamento(s) restaurado(s) com sucesso.`, AlertType.success);
          this.carregarIgnorados(true);
        },
        error: (err) => {
          this.isRestaurando = false;
          this.alertService.show('Erro', err.message, AlertType.error);
        }
      });
    });
  }

  restaurarUnico(item: ExtratoBancarioIgnoradoItem): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '400px',
      data: {
        title: 'Restaurar lançamento',
        innerHtml: `<p>Deseja restaurar o lançamento <strong>${item.memo || item.fitid}</strong>?</p><p>Ele voltará a aparecer na lista de pendências para conciliação.</p>`,
        buttonConfirm: 'Restaurar',
        buttonClose: 'Cancelar',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.conciliacaoBancariaService.restaurarIgnorados({ idsExtratoBancario: [item.idExtratoBancario] }).subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
            return;
          }
          this.alertService.show('Sucesso', 'Lançamento restaurado com sucesso.', AlertType.success);
          this.carregarIgnorados(true);
        },
        error: (err) => {
          this.alertService.show('Erro', err.message, AlertType.error);
        }
      });
    });
  }

  labelConta(conta: ExtratoBancarioContaItem): string {
    return `${conta.nomeBanco} — ${conta.descricao} (Ag. ${conta.agencia})`;
  }

  voltar(): void {
    const conta = this._contas.find(c => c.idContaFinanceira === this.idContaFinanceira);
    const rota = conta?.idTipoConta === TipoContaEnum.CartaoCredito
      ? '/financas/conciliacao-bancaria/cartao/lancamentos'
      : '/financas/conciliacao-bancaria/lancamentos';
    this.router.navigate([rota, this.idContaFinanceira]);
  }
}
